import { useState } from 'react';
import TopNavigation from '../../components/bar/TopNavigation';
import useArtistQueryLikedOnly from '../../hooks/queries/useArtistQueryLikedOnly';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import ArtistCard from './ArtistCard';
import { t } from 'i18next';
import type { ImageSearchResultType } from '../types/type';
import type { ArtistType, MemberType } from '../../types/ArtistType';
import LikeShapeIcon from '../../../components/icons/LikeShapeIcon';
import { Button } from '@peacecover/component';

export default function ImageSearchSelectMember({
  onClose,
  onMemberSelect
}: {
  onClose: () => void;
  onMemberSelect: ({
    artistId,
    memberId,
    memberName,
    artistName
  }: ImageSearchResultType['selectedMember']) => void;
}): JSX.Element {
  const { data: artists } = useArtistQueryLikedOnly();
  const [selectedArtist, setSelectedArtist] = useState<ArtistType | null>(null);
  const [selectedMember, setSelectedMember] = useState<MemberType | null>(null);

  const memberList = selectedArtist?.members;

  return (
    <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-white dark:bg-background-dark flex flex-col h-screen overflow-hidden">
      <div className="flex flex-col h-full">
        <TopNavigation
          type="close"
          onCloseClick={onClose}
          centerComponent={<p className="text-[17px] font-bold">{t('search')}</p>}
        />

        <HorizontalScroll
          className="w-full h-[120px] overflow-y-hidden"
          listClassName="gap-[20px] p-[20px]"
        >
          {artists?.map((artist) => (
            <ArtistCard
              key={artist.artistId}
              artist={artist}
              selected={selectedArtist?.artistId === artist.artistId}
              setSelectedArtist={() => setSelectedArtist(artist)}
            />
          ))}
        </HorizontalScroll>

        <p className="text-[26px] font-bold p-[20px]">{t('imageSearch.selectMember.title')}</p>

        <div className="flex-1 overflow-y-auto ">
          <div className="flex flex-col gap-[16px] pb-[20px] px-[20px]">
            {memberList?.map((member) => (
              <MemberItem
                key={member.memberId}
                member={member}
                isSelected={member.memberId === selectedMember?.memberId}
                setSelectedMember={setSelectedMember}
              />
            ))}
          </div>
        </div>
        <div className="px-[20px] pt-[20px]">
          <Button
            size="large"
            className="w-full"
            disabled={!selectedMember}
            onClick={() =>
              onMemberSelect({
                artistId: selectedArtist?.artistId || '',
                artistName: selectedArtist?.name || '',
                memberId: selectedMember?.memberId || '',
                memberName: selectedMember?.name || ''
              })
            }
          >
            {t('confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

const MemberItem = ({
  member,
  isSelected,
  setSelectedMember
}: {
  member: MemberType;
  isSelected: boolean;
  setSelectedMember: (member: MemberType) => void;
}) => {
  return (
    <button
      key={member.memberId}
      type="button"
      onClick={() => setSelectedMember(member)}
      className="w-full flex justify-start items-center"
    >
      <p
        className={`text-[40px] leading-[48px] font-extrabold ${
          isSelected
            ? 'text-primary-light dark:text-primary-dark'
            : 'text-disabled-light dark:text-separator-dark-1'
        }`}
      >
        {member.name}
      </p>
      {member.isLiked && (
        <LikeShapeIcon
          width={42}
          height={42}
          className={`ml-[3px] ${isSelected ? 'fill-primary-light dark:fill-primary-dark' : 'fill-disabled-light dark:fill-separator-dark-1'}`}
        />
      )}
    </button>
  );
};
